// Patches a container to match new HTML without replacing what didn't change, so a
// live update can't wipe hover, focus, scroll or a half-finished click. Children with
// a data-key (or id) are matched by it, so a boat that moves keeps its element; other
// children are matched by position and tag. Wire handlers with el.onclick = ... (not
// addEventListener): a kept element then gets the fresh handler instead of a second one.

// While collectChanges() runs, keyed elements that were added or whose content changed.
let collector = null;

// patchNodes() takes elements built in code, whose handlers are properties (el.onclick =).
// A kept element takes the new element's handlers, so it never acts on stale data.
const HANDLERS = ['onclick', 'ondragstart', 'ondragend', 'ondragenter', 'ondragover', 'ondragleave', 'ondrop', 'oninput', 'onchange', 'onkeydown', 'onpointerdown'];

function keyOf(node) {
  if (node.nodeType !== 1) return '';
  return node.getAttribute('data-key') || node.id || '';
}

function syncAttributes(from, to) {
  let changed = false;
  for (const { name } of [...from.attributes]) {
    if (!to.hasAttribute(name)) { from.removeAttribute(name); changed = true; }
  }
  for (const { name, value } of [...to.attributes]) {
    // The live-update glow (liveView.js) is added at runtime: keep it, and don't count it as a change.
    const want = name === 'class' && from.classList.contains('live-changed') ? `${value} live-changed` : value;
    if (from.getAttribute(name) !== want) { from.setAttribute(name, want); changed = true; }
  }
  // Form state lives in properties once a user has touched it; leave a focused field alone.
  if ((from.tagName === 'INPUT' || from.tagName === 'TEXTAREA' || from.tagName === 'SELECT') && document.activeElement !== from) {
    if ('checked' in from) from.checked = to.hasAttribute('checked');
    if (from.tagName !== 'SELECT' && from.type !== 'checkbox' && from.type !== 'radio') from.value = to.getAttribute('value') ?? to.textContent ?? '';
  }
  return changed;
}

// Returns whether anything inside `from` changed.
function morphNode(from, to) {
  if (from.nodeType !== 1) {
    if (from.nodeValue === to.nodeValue) return false;
    from.nodeValue = to.nodeValue;
    return true;
  }
  const attrs = syncAttributes(from, to);
  for (const h of HANDLERS) if (to[h]) from[h] = to[h];
  const kids = morphChildren(from, to);
  return attrs || kids;
}

function morphChildren(from, to) {
  const oldKeyed = new Map();
  from.childNodes.forEach(c => { const k = keyOf(c); if (k) oldKeyed.set(k, c); });
  let cursor = from.firstChild;
  let changed = false;
  for (const next of [...to.childNodes]) {
    const k = keyOf(next);
    let match = null;
    if (k) {
      const old = oldKeyed.get(k);
      if (old && old.nodeName === next.nodeName) { match = old; oldKeyed.delete(k); }
    } else if (cursor && !keyOf(cursor) && cursor.nodeType === next.nodeType && cursor.nodeName === next.nodeName) {
      match = cursor;
    }
    if (match) {
      if (match === cursor) cursor = cursor.nextSibling;
      else { from.insertBefore(match, cursor); changed = true; } // a move alone isn't flagged
      if (morphNode(match, next)) {
        changed = true;
        if (k && collector) collector.add(match);
      }
    } else {
      from.insertBefore(next, cursor);
      changed = true;
      if (k && collector) collector.add(next);
    }
  }
  while (cursor) {
    const after = cursor.nextSibling;
    from.removeChild(cursor);
    cursor = after;
    changed = true;
  }
  return changed;
}

/** Makes container's children match html, touching only what differs. */
export function patchHTML(container, html) {
  const tpl = document.createElement('template');
  tpl.innerHTML = html;
  morphChildren(container, tpl.content);
}

/** Makes container's children match `nodes` (elements built in code), touching only what differs. */
export function patchNodes(container, nodes) {
  const frag = document.createDocumentFragment();
  nodes.forEach(n => frag.appendChild(n));
  morphChildren(container, frag);
}

/** Runs fn (which patches) and returns the keyed elements it added or changed. */
export function collectChanges(fn) {
  const outer = collector;
  collector = new Set();
  try {
    fn();
    return [...collector];
  } finally {
    collector = outer;
  }
}
